import { useState } from 'react';
import { adminApi, clubsApi } from '../../../lib/api';
import { toast } from 'sonner';

const USERS_PER_PAGE = 20;

export const useUsersActions = ({ isItalian, user, refreshUser, clubs, setClubs, fetchUsersCore }) => {
  // Core user state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [roleCounts, setRoleCounts] = useState({});
  const [usersPage, setUsersPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Multi-select
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Coins dialog
  const [showCoinsDialog, setShowCoinsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [coinsAmount, setCoinsAmount] = useState(0);
  const [coinsAction, setCoinsAction] = useState('add');
  const [coinsTarget, setCoinsTarget] = useState('personal');

  // Send coins
  const [showSendCoinsDialog, setShowSendCoinsDialog] = useState(false);
  const [coinsRecipient, setCoinsRecipient] = useState(null);
  const [coinsForm, setCoinsForm] = useState({ amount: '', reason: '', target: 'personal' });
  const [sendingCoins, setSendingCoins] = useState(false);

  // Ban
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [showSelfBanDialog, setShowSelfBanDialog] = useState(false);

  // Delete
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Password
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Create user
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ email: '', gamertag: '', password: '', phone: '', birth_date: '', role: 'player', player_role: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  // --- Fetch ---
  const fetchUsers = async (page = usersPage, search = searchQuery) => {
    try {
      const params = { skip: page * USERS_PER_PAGE, limit: USERS_PER_PAGE };
      if (search) params.search = search;
      const res = await adminApi.getUsers(params);
      const data = res.data?.users || res.data || [];
      setUsers(data);
      setUsersTotal(res.data?.total || data.length);
      if (res.data?.role_counts) setRoleCounts(res.data.role_counts);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // --- Role ---
  const handleUpdateRole = async (userId, newRole) => {
    try {
      await adminApi.updateUser(userId, { role: newRole });
      setUsers(prev => {
        const old = prev.find(u => u.user_id === userId);
        if (old && old.role !== newRole) {
          setRoleCounts(rc => ({
            ...rc,
            [old.role]: Math.max(0, (rc[old.role] || 0) - 1),
            [newRole]: (rc[newRole] || 0) + 1,
          }));
        }
        return prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u);
      });
      toast.success(isItalian ? 'Ruolo aggiornato!' : 'Role updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  // --- Ban ---
  const handleToggleBan = (userId) => {
    if (userId === user.user_id) { setShowSelfBanDialog(true); return; }
    const targetUser = users.find(u => u.user_id === userId);
    setUserToBan(targetUser);
    setShowBanDialog(true);
  };

  const confirmBanUser = async () => {
    if (!userToBan) return;
    try {
      await adminApi.updateUser(userToBan.user_id, { is_banned: !userToBan.is_banned });
      setUsers(prev => prev.map(u => u.user_id === userToBan.user_id ? { ...u, is_banned: !u.is_banned } : u));
      toast.success(userToBan.is_banned
        ? (isItalian ? 'Utente sbloccato' : 'User unbanned')
        : (isItalian ? 'Utente bannato' : 'User banned'));
      setShowBanDialog(false);
      setUserToBan(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  const confirmSelfBan = async () => {
    try {
      await adminApi.updateUser(user.user_id, { is_banned: true });
      setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, is_banned: true } : u));
      setShowSelfBanDialog(false);
      toast.success(isItalian ? 'Ti sei bannato. Verrai disconnesso.' : 'You banned yourself. You will be logged out.');
      setTimeout(() => { window.location.href = '/login'; }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  // --- Delete ---
  const handleDeleteUser = (userItem) => { setUserToDelete(userItem); setShowDeleteUserDialog(true); };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await adminApi.deleteUser(userToDelete.user_id);
      setUsers(prev => prev.filter(u => u.user_id !== userToDelete.user_id));
      toast.success(isItalian ? `Utente "${userToDelete.name}" eliminato!` : `User "${userToDelete.name}" deleted!`);
      setShowDeleteUserDialog(false); setUserToDelete(null);
      const clubsRes = await clubsApi.list();
      setClubs(clubsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    } finally {
      setDeleting(false);
    }
  };

  // --- Password ---
  const handleOpenChangePassword = (userItem) => {
    setPasswordTarget(userItem); setNewPassword(''); setShowChangePasswordDialog(true);
  };

  const confirmChangePassword = async () => {
    if (!passwordTarget || !newPassword.trim()) return;
    if (newPassword.length < 4) {
      toast.error(isItalian ? 'La password deve avere almeno 4 caratteri' : 'Password must be at least 4 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const res = await adminApi.adminChangePassword(passwordTarget.user_id, newPassword);
      toast.success(res.data.message);
      setShowChangePasswordDialog(false); setPasswordTarget(null); setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore cambio password' : 'Password change error'));
    } finally {
      setChangingPassword(false);
    }
  };

  // --- Coins ---
  const handleOpenModifyCoins = (u) => {
    setSelectedUser(u);
    setCoinsAmount(0);
    setCoinsAction('add');
    setCoinsTarget('personal');
    setShowCoinsDialog(true);
  };

  const handleModifyCoins = async () => {
    if (!selectedUser || coinsAmount <= 0) return;
    try {
      if (coinsTarget === 'club') {
        const club = clubs.find(c => c.captain_id === selectedUser.user_id);
        if (!club) { toast.error(isItalian ? 'Utente non ha un club' : 'User has no club'); return; }
        await adminApi.modifyCoins({
          club_id: club.club_id, target_type: 'club',
          amount: coinsAction === 'add' ? coinsAmount : -coinsAmount,
          reason: `Admin modification by ${user.gamertag || user.name}`
        });
        const clubsRes = await clubsApi.list({ include_logo: false });
        setClubs(clubsRes.data);
      } else {
        await adminApi.modifyCoins({
          user_id: selectedUser.user_id, target_type: 'personal',
          amount: coinsAction === 'add' ? coinsAmount : -coinsAmount,
          reason: `Admin modification by ${user.gamertag || user.name}`
        });
        await fetchUsers();
      }
      setShowCoinsDialog(false); setSelectedUser(null); setCoinsAmount(0); setCoinsTarget('personal');
      toast.success(isItalian ? 'Coins modificati!' : 'Coins modified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    }
  };

  // --- Send Coins ---
  const handleOpenSendCoins = (userToSend) => {
    setCoinsRecipient(userToSend);
    setCoinsForm({ amount: '', reason: '', target: 'personal' });
    setShowSendCoinsDialog(true);
  };

  const handleSendCoins = async () => {
    if (!coinsRecipient || !coinsForm.amount || !coinsForm.reason) {
      toast.error(isItalian ? 'Compila tutti i campi' : 'Fill all fields'); return;
    }
    const amount = parseInt(coinsForm.amount);
    if (isNaN(amount) || amount === 0) { toast.error(isItalian ? 'Importo non valido' : 'Invalid amount'); return; }

    setSendingCoins(true);
    try {
      const data = { amount, reason: coinsForm.reason, target_type: coinsForm.target };
      if (coinsForm.target === 'personal') {
        data.user_id = coinsRecipient.user_id;
      } else {
        const userClub = clubs.find(c => c.captain_id === coinsRecipient.user_id);
        if (!userClub) { toast.error(isItalian ? 'Utente non è capitano di nessun club' : 'User is not captain of any club'); setSendingCoins(false); return; }
        data.club_id = userClub.club_id;
      }
      await adminApi.modifyCoins(data);
      toast.success(isItalian
        ? `${amount > 0 ? 'Inviati' : 'Rimossi'} ${Math.abs(amount).toLocaleString()} coins!`
        : `${amount > 0 ? 'Sent' : 'Removed'} ${Math.abs(amount).toLocaleString()} coins!`);

      if (coinsForm.target === 'personal') {
        setUsers(prev => prev.map(u =>
          u.user_id === coinsRecipient.user_id ? { ...u, personal_coins: (u.personal_coins || 0) + amount } : u
        ));
        if (coinsRecipient.user_id === user?.user_id) await refreshUser();
      } else {
        const userClub = clubs.find(c => c.captain_id === coinsRecipient.user_id);
        if (userClub) {
          setClubs(prev => prev.map(c =>
            c.club_id === userClub.club_id ? { ...c, coins: (c.coins || 0) + amount } : c
          ));
        }
      }
      setShowSendCoinsDialog(false); setCoinsRecipient(null);
      setCoinsForm({ amount: '', reason: '', target: 'personal' });
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally { setSendingCoins(false); }
  };

  // --- Release player from club (super admin) ---
  const handleReleasePlayer = async (userItem) => {
    if (!userItem?.club_id) return;
    const clubName = clubs.find(c => c.club_id === userItem.club_id)?.name || 'club';
    const msg = isItalian
      ? `Vuoi svincolare ${userItem.gamertag || userItem.name} da ${clubName}? L'azione è gratuita (azione amministrativa) e termina il contratto attivo.`
      : `Release ${userItem.gamertag || userItem.name} from ${clubName}? This admin action is free and terminates the active contract.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await clubsApi.releasePlayer(userItem.club_id, userItem.user_id);
      toast.success(res.data?.message || (isItalian ? 'Giocatore svincolato!' : 'Player released!'));
      setUsers(prev => prev.map(u => u.user_id === userItem.user_id ? { ...u, club_id: null } : u));
      const clubsRes = await clubsApi.list({ include_logo: false });
      setClubs(clubsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore svincolo' : 'Release error'));
    }
  };

  // --- Multi-select ---
  const filteredUsers = users;

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectableUsers = filteredUsers.filter(u => u.user_id !== user.user_id);
    if (selectedUserIds.size === selectableUsers.length && selectableUsers.length > 0) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(selectableUsers.map(u => u.user_id)));
    }
  };

  const confirmBulkDeleteUsers = async () => {
    if (selectedUserIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await adminApi.bulkDeleteUsers([...selectedUserIds]);
      const { deleted_count, errors } = res.data;
      if (deleted_count > 0) toast.success(isItalian ? `${deleted_count} utenti eliminati!` : `${deleted_count} users deleted!`);
      if (errors?.length > 0) errors.forEach(err => toast.error(err));
      setSelectedUserIds(new Set());
      setShowBulkDeleteDialog(false);
      const [clubsRes] = await Promise.all([clubsApi.list({ include_logo: false })]);
      await fetchUsers();
      setClubs(clubsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    } finally { setBulkDeleting(false); }
  };

  // --- Create User ---
  const handleCreateUser = async () => {
    if (!createUserForm.email.trim() || !createUserForm.gamertag.trim() || !createUserForm.password.trim()) {
      toast.error(isItalian ? 'Email, gamertag e password sono obbligatori' : 'Email, gamertag and password are required');
      return;
    }
    setCreatingUser(true);
    try {
      await adminApi.createUser(createUserForm);
      toast.success(isItalian ? 'Utente creato con successo!' : 'User created successfully!');
      setShowCreateUserDialog(false);
      setCreateUserForm({ email: '', gamertag: '', password: '', phone: '', birth_date: '', role: 'player', player_role: '' });
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore nella creazione utente' : 'Error creating user'));
    } finally {
      setCreatingUser(false);
    }
  };

  return {
    // Data
    users, setUsers, usersTotal, usersPage, searchQuery, setSearchQuery,
    roleCounts,
    filteredUsers, USERS_PER_PAGE,
    fetchUsers,
    onPageChange: (page) => { setUsersPage(page); fetchUsers(page, searchQuery); },

    // Role & Ban
    handleUpdateRole, handleToggleBan,
    showBanDialog, setShowBanDialog, userToBan, setUserToBan, confirmBanUser,
    showSelfBanDialog, setShowSelfBanDialog, confirmSelfBan,

    // Delete
    handleDeleteUser,
    showDeleteUserDialog, setShowDeleteUserDialog, userToDelete, setUserToDelete, confirmDeleteUser,
    deleting,

    // Release player
    handleReleasePlayer,

    // Multi-select
    selectedUserIds, toggleSelectAll, toggleUserSelection,
    showBulkDeleteDialog, setShowBulkDeleteDialog, bulkDeleting, confirmBulkDeleteUsers,

    // Coins
    showCoinsDialog, setShowCoinsDialog, selectedUser, setSelectedUser,
    coinsAmount, setCoinsAmount, coinsAction, setCoinsAction, coinsTarget, setCoinsTarget,
    handleModifyCoins,
    handleOpenModifyCoins,

    // Send Coins
    handleOpenSendCoins,
    showSendCoinsDialog, setShowSendCoinsDialog, coinsRecipient, setCoinsRecipient,
    coinsForm, setCoinsForm, sendingCoins, handleSendCoins,

    // Password
    handleOpenChangePassword,
    showChangePasswordDialog, setShowChangePasswordDialog, passwordTarget, setPasswordTarget,
    newPassword, setNewPassword, changingPassword, confirmChangePassword,

    // Create User
    showCreateUserDialog, setShowCreateUserDialog, createUserForm, setCreateUserForm,
    creatingUser, handleCreateUser,
  };
};
